import { useState, useEffect, useRef } from 'react';
import {
    Images, Languages, Loader2, MessageSquareText, MousePointerClick, Save, SlidersHorizontal,
} from 'lucide-react';
import { TabSkeleton } from "../BotManagement/BotManagementSkeleton";
import { useParams } from 'react-router-dom';
import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { MediaManager } from '../../../components/MediaManager/MediaManager';
import TextForm from '../../../components/TextForm/TextForm';
import PushTip from '../../../components/UI/PushTip/PushTip'
import MarketIcon from '../../../components/Flags/MarketIcon';
import { normalizeMode } from '../../../utils/market';
import MessagesEditor from './MessagesEditor';
import PaymentOptions from './PaymentOptions';
import SocialProofEditor from './SocialProofEditor';
import PaymentSection, { OptionRow, Switch } from './PaymentSection';

const NO_PROOF = { enabled: false, list: [''], interval: 30 };
// Prova social de um mercado vinda da API → estado do editor.
const proofFromApi = (cfg) => (cfg ? { enabled: !!cfg.enabled, list: cfg.list?.length ? cfg.list : [''], interval: cfg.interval || 30 } : NO_PROOF);
// Estado do editor → API (sem linhas vazias).
const proofToApi = (p) => ({ enabled: p.enabled, list: p.list.filter((t) => t.trim()), interval: Number(p.interval) || 30 });

// Mercados da aba: Brasil (PIX) e os textos das telas do Global e do LATAM.
// Idioma em que o comprador lê as mensagens de cada mercado.
const BUYER_LANGUAGE = { brl: 'Comprador em português', usd: 'Comprador em inglês', mxn: 'Comprador em espanhol' };

const MARKET_TABS = [
    { mode: 'brl', label: 'Brasil' },
    { mode: 'usd', label: 'Global' },
    { mode: 'mxn', label: 'LATAM' },
];

// Telas com modelo por mercado (o LATAM não tem tela de forma de pagamento).
const SCREEN_KEYS = { usd: ['method', 'currency', 'secure'], mxn: ['currency', 'secure'] };

export default function PixConfig() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();
    const mediaRef = useRef();
    // Mídias antes do pagamento no Global e no LATAM (uma lista por mercado).
    const marketMediaRefs = { usd: useRef(), mxn: useRef() };

    const [loading, setLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    // Mercado aberto na aba (começa no do bot) e textos do Global/LATAM.
    const [market, setMarket] = useState('brl');
    const [cardTexts, setCardTexts] = useState({ usd: {}, mxn: {} });
    const [textDefaults, setTextDefaults] = useState({ usd: {}, mxn: {} });

    // Mensagem PIX
    const [defaultTemplate, setDefaultTemplate] = useState('');
    const [template, setTemplate] = useState('');
    const [codeFormat, setCodeFormat] = useState('blockquote');

    // Mídias
    const [mediaList, setMediaList] = useState([]);
    const [sendMode, setSendMode] = useState('agrupado');

    // Botões
    const [showVerify, setShowVerify] = useState(true);
    const [showCopy, setShowCopy] = useState(true);
    const [showQr, setShowQr] = useState(true);
    const [verifyText, setVerifyText] = useState('');
    const [copyText, setCopyText] = useState('');
    const [qrText, setQrText] = useState('');
    const [showQrCode, setShowQrCode] = useState(true);

    // Prova Social: a do PIX (Brasil) e a de cada mercado (Global, LATAM)
    const [pixProof, setPixProof] = useState(NO_PROOF);
    const [marketProof, setMarketProof] = useState({ usd: NO_PROOF, mxn: NO_PROOF });

    // Global/LATAM: mídias (null no LATAM = ainda usa as do PIX) e mensagens do SPEI/Nequi.
    const [marketMedia, setMarketMedia] = useState({ usd: { list: [], send_mode: 'agrupado' }, mxn: { list: null, send_mode: 'agrupado' } });
    const [localTemplates, setLocalTemplates] = useState({ spei: '', nequi: '' });
    const [localDefaults, setLocalDefaults] = useState({ spei: '', nequi: '' });
    // Telas "Escolha da moeda" e "Link de pagamento" (modelo, como o SPEI), por mercado.
    const emptyScreens = { usd: { method: '', currency: '', secure: '' }, mxn: { currency: '', secure: '' } };
    const [screenTemplates, setScreenTemplates] = useState(emptyScreens);
    // Opções das telas do Global/LATAM (as mesmas nas duas abas).
    const [payOptions, setPayOptions] = useState({ simple: false, skip: false });
    const [screenDefaults, setScreenDefaults] = useState(emptyScreens);
    const [buttonsOff, setButtonsOff] = useState([]);
    // Aba de mensagem a abrir (ex.: SPEI recusado ao salvar).
    const [messageFocus, setMessageFocus] = useState(null);

    useEffect(() => {
        if (!botId) return;
        loadData();
    }, [botId]);

    const loadData = async () => {
        try {
            setLoadingPage(true);
            const { data } = await authService.bots.edit_bot(botId);
            const pc = data.bot?.pix_config || {};

            setMarket(normalizeMode(pc.currency_mode));
            setCardTexts({ usd: pc.card_texts?.usd || {}, mxn: pc.card_texts?.mxn || {} });
            setTextDefaults({ usd: pc.card_text_defaults?.usd || {}, mxn: pc.card_text_defaults?.mxn || {} });
            setDefaultTemplate(pc.default_template || '');
            setTemplate(pc.message_template || '');
            setCodeFormat(pc.code_format || 'blockquote');
            setMediaList(pc.media_list || []);
            setSendMode(pc.media_send_mode || 'agrupado');
            setShowVerify(pc.show_verify_button !== false);
            setShowCopy(pc.show_copy_button !== false);
            setShowQr(pc.show_qr_button !== false);
            setVerifyText(pc.verify_button_text || '');
            setCopyText(pc.copy_button_text || '');
            setQrText(pc.qr_button_text || '');
            setPixProof({
                enabled: !!pc.social_proof_enabled,
                list: pc.social_proof_list?.length ? pc.social_proof_list : [''],
                interval: pc.social_proof_interval || 30,
            });
            setMarketProof({ usd: proofFromApi(pc.market_social_proof?.usd), mxn: proofFromApi(pc.market_social_proof?.mxn) });
            setMarketMedia({
                usd: { list: pc.market_media?.usd?.list || [], send_mode: pc.market_media?.usd?.send_mode || 'agrupado' },
                mxn: { list: pc.market_media?.mxn?.list ?? null, send_mode: pc.market_media?.mxn?.send_mode || 'agrupado' },
            });
            const defaults = pc.local_template_defaults || {};
            setLocalDefaults({ spei: defaults.spei || '', nequi: defaults.nequi || '' });
            const screenDefs = pc.screen_template_defaults || {};
            const screensOf = (fill) => Object.fromEntries(['usd', 'mxn'].map((m) => [m,
                Object.fromEntries(SCREEN_KEYS[m].map((k) => [k, fill(m, k)]))]));
            setScreenDefaults(screensOf((m, k) => screenDefs[m]?.[k] || ''));
            setPayOptions({ simple: pc.simple_pay_buttons === true, skip: pc.skip_method_screen === true });
            // Sem modelo próprio, o editor começa no padrão (edita a partir dele).
            setScreenTemplates(screensOf((m, k) => pc.screen_templates?.[m]?.[k] || screenDefs[m]?.[k] || ''));
            // Sem modelo próprio, o editor começa no padrão (edita a partir dele).
            setButtonsOff(pc.local_buttons_off || []);
            setLocalTemplates({
                spei: pc.local_templates?.spei || defaults.spei || '',
                nequi: pc.local_templates?.nequi || defaults.nequi || '',
            });
        } catch (err) {
            showError('Erro ao carregar configuração PIX');
        } finally {
            setLoadingPage(false);
        }
    };

    // Opções de "Botões e telas": salvam na hora (otimista; volta se a API recusar).
    const OPTION_FIELDS = { simple: 'simple_pay_buttons', skip: 'skip_method_screen' };
    const changePayOptions = async (next) => {
        const prev = payOptions;
        const changed = Object.keys(OPTION_FIELDS).find((k) => next[k] !== prev[k]);
        setPayOptions(next);
        if (!changed) return;
        try {
            await authService.bots.save_pix_config(botId, { [OPTION_FIELDS[changed]]: next[changed] });
            showSuccess(next[changed] ? 'Opção ligada' : 'Opção desligada');
        } catch (err) {
            setPayOptions(prev);
            showError(err?.response?.data?.error || err?.message || 'Erro ao salvar a opção');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // {pix_code} é obrigatório no template
        if (template.trim() && !template.includes('{pix_code}')) {
            showError('O template deve conter a variável {pix_code}');
            return;
        }
        if (localTemplates.spei.trim() && !localTemplates.spei.includes('{clabe}')) {
            setMarket('mxn');
            setMessageFocus('spei');
            showError('A mensagem do SPEI deve conter a variável {clabe}');
            return;
        }

        setLoading(true);
        try {
            const pix = proofToApi(pixProof);
            await authService.bots.save_pix_config(botId, {
                card_texts: cardTexts,
                // Igual ao padrão = sem modelo próprio (acompanha melhorias do padrão).
                local_templates: {
                    spei: localTemplates.spei.trim() === localDefaults.spei.trim() ? '' : localTemplates.spei,
                    nequi: localTemplates.nequi.trim() === localDefaults.nequi.trim() ? '' : localTemplates.nequi,
                },
                local_buttons_off: buttonsOff,
                // Igual ao padrão = sem modelo próprio (segue os textos/padrão do bot).
                screen_templates: Object.fromEntries(['usd', 'mxn'].map((m) => [m, Object.fromEntries(
                    SCREEN_KEYS[m].map((k) => [k,
                        screenTemplates[m][k].trim() === screenDefaults[m][k].trim() ? '' : screenTemplates[m][k]]),
                )])),
                market_media_send_mode: { usd: marketMedia.usd.send_mode, mxn: marketMedia.mxn.send_mode },
                message_template: template.trim() || null,
                media_send_mode: sendMode,
                code_format: codeFormat,
                show_verify_button: showVerify,
                show_copy_button: showCopy,
                show_qr_button: showQr,
                verify_button_text: verifyText.trim() || null,
                copy_button_text: copyText.trim() || null,
                qr_button_text: qrText.trim() || null,
                social_proof_enabled: pix.enabled,
                social_proof_list: pix.list,
                social_proof_interval: pix.interval,
                market_social_proof: { usd: proofToApi(marketProof.usd), mxn: proofToApi(marketProof.mxn) },
                show_qr_code: showQrCode,
            });

            for (const ref of [mediaRef, marketMediaRefs.usd, marketMediaRefs.mxn]) {
                if (ref.current) await ref.current.uploadAll();
            }

            showSuccess('Pagamentos salvos!');
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Erro ao salvar';
            showError(msg);
        } finally {
            setLoading(false);
        }
    };


    if (loadingPage) {
        return <TabSkeleton tab="pix" />;
    }

    const marketMediaSection = (m) => (
        <PaymentSection
            icon={Images}
            title="Mídias do pagamento"
            subtitle={m === 'mxn' && marketMedia.mxn.list === null
                ? 'Enviadas antes do pagamento. Sem mídias próprias, usa as do PIX.'
                : 'Enviadas antes do pagamento.'}
        >
            <MediaManager
                ref={marketMediaRefs[m]}
                botId={botId}
                targetType="bot"
                targetId={botId}
                multiMedia={true}
                mediaList={marketMedia[m].list || []}
                onMediaChange={(list) => setMarketMedia((prev) => ({ ...prev, [m]: { ...prev[m], list } }))}
                sendMode={marketMedia[m].send_mode}
                onSendModeChange={(mode) => setMarketMedia((prev) => ({ ...prev, [m]: { ...prev[m], send_mode: mode } }))}
                slotOverride={m === 'usd' ? 'global_multi' : 'latam_multi'}
                mediaLabel={m === 'usd' ? 'do Global' : 'do LATAM'}
            />
        </PaymentSection>
    );

    return (
        <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <div className="spt-form-bot">

                {/* MERCADO: Brasil (PIX) ou as telas do Global/LATAM */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
                    <div role="tablist" aria-label="Mercado" className="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-white/10 bg-white/[0.03]">
                        {MARKET_TABS.map((t) => (
                            <button key={t.mode} type="button" role="tab" aria-selected={market === t.mode}
                                onClick={() => setMarket(t.mode)}
                                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                    market === t.mode ? 'bg-white/10 text-white ring-1 ring-white/20' : 'text-white/55 hover:text-white hover:bg-white/[0.05]'
                                }`}>
                                <MarketIcon mode={t.mode} />
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <span className="pset-chip">
                        <Languages size={12} />
                        {BUYER_LANGUAGE[market]}
                    </span>
                </div>

                {/* Global e LATAM ficam montados (escondidos) como o Brasil: mídias
                    escolhidas e ainda não enviadas não se perdem ao trocar de aba. */}
                {['usd', 'mxn'].map((m) => (
                    <div key={m} hidden={market !== m} className="pset-stack">
                        <PaymentSection icon={SlidersHorizontal} title="Botões e telas"
                            subtitle="Como o comprador escolhe a moeda.">
                            <PaymentOptions market={m} values={payOptions} onChange={changePayOptions} />
                        </PaymentSection>

                        {marketMediaSection(m)}

                        <PaymentSection icon={MessageSquareText} title="Mensagens"
                            subtitle="Na ordem em que o comprador vê.">
                            <MessagesEditor
                                market={m}
                                templates={localTemplates} templateDefaults={localDefaults} onTemplatesChange={setLocalTemplates}
                                screenTemplates={screenTemplates[m]} screenDefaults={screenDefaults[m]}
                                onScreenTemplatesChange={(next) => setScreenTemplates((prev) => ({ ...prev, [m]: next }))}
                                texts={cardTexts[m]} textDefaults={textDefaults[m]}
                                onTextsChange={(texts) => setCardTexts((prev) => ({ ...prev, [m]: texts }))}
                                buttonsOff={buttonsOff} onButtonsOffChange={setButtonsOff}
                                focus={m === 'mxn' ? messageFocus : null}
                                simpleButtons={payOptions.simple}
                            />
                        </PaymentSection>

                        <SocialProofEditor
                            market={m === 'usd' ? 'Global' : 'LATAM'}
                            value={marketProof[m]}
                            onChange={(proof) => setMarketProof((prev) => ({ ...prev, [m]: proof }))}
                            placeholder={m === 'usd' ? 'Social proof' : 'Prueba social'}
                            hint={`Mensagem que alterna depois do pagamento gerado. Escreva ${m === 'usd' ? 'em inglês' : 'em espanhol'}.`}
                        />
                    </div>
                ))}

                {/* Brasil fica montado (escondido) para não perder mídias ainda não enviadas. */}
                <div hidden={market !== 'brl'} className="pset-stack">
                    <PaymentSection icon={Images} title="Mídias do PIX" subtitle="Enviadas antes do pagamento.">
                        <MediaManager
                            ref={mediaRef}
                            botId={botId}
                            targetType="bot"
                            targetId={botId}
                            multiMedia={true}
                            mediaList={mediaList}
                            onMediaChange={setMediaList}
                            sendMode={sendMode}
                            onSendModeChange={setSendMode}
                            slotOverride="pix_multi"
                            mediaLabel="do PIX"
                        />
                    </PaymentSection>

                    <PaymentSection icon={MessageSquareText} title="Mensagem do PIX"
                        subtitle="Enviada com o código PIX.">
                        <TextForm
                            value={template}
                            onChange={setTemplate}
                            variables={['plano', 'duration', 'valor', 'pix_code']}
                        />
                        <PushTip>A variável <strong>{'{pix_code}'}</strong> é obrigatória quando o template for preenchido.</PushTip>
                        <div className="mt-4 max-w-xs">
                            <label className="pset-field-label" htmlFor="pix-code-format">Formato do código</label>
                            <select id="pix-code-format" className="form-select" value={codeFormat}
                                onChange={(e) => setCodeFormat(e.target.value)}>
                                <option value="blockquote">Citação (padrão)</option>
                                <option value="text">Texto (monospace)</option>
                                <option value="code_block">Bloco de código</option>
                            </select>
                        </div>
                    </PaymentSection>

                    <PaymentSection icon={MousePointerClick} title="Botões"
                        subtitle="Desligado não aparece. Texto vazio usa o padrão.">
                        <ToggleRow label="Verificar pagamento" checked={showVerify} onToggle={setShowVerify}
                            textValue={verifyText} onTextChange={setVerifyText} placeholder="🔄Verificar Pagamento" />
                        <ToggleRow label="Copiar código" checked={showCopy} onToggle={setShowCopy}
                            textValue={copyText} onTextChange={setCopyText} placeholder="📋 Copiar Código" />
                        <ToggleRow label="Ver QR Code" checked={showQr} onToggle={setShowQr}
                            textValue={qrText} onTextChange={setQrText} placeholder="📱Ver QR Code" />
                        <div className="pset-options mt-4 pt-4 border-t border-white/[0.06]">
                            <OptionRow title="Mostrar QR Code" hint="Envia a imagem do QR junto com o código."
                                checked={showQrCode} onChange={setShowQrCode} />
                        </div>
                    </PaymentSection>

                    <SocialProofEditor
                        value={pixProof}
                        onChange={setPixProof}
                        hint="Mensagem que alterna depois do PIX gerado."
                    />
                </div>

                <div className="pset-footer">
                    <button disabled={loading} className="btn-primary inline-flex items-center">
                        {loading ? (
                            <><Loader2 size={14} className="animate-spin me-2" />Salvando...</>
                        ) : (
                            <><Save size={14} className="me-2" />Salvar pagamentos</>
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
}

function ToggleRow({ label, checked, onToggle, textValue, onTextChange, placeholder }) {
    return (
        <div className="pset-button-row">
            <Switch checked={checked} onChange={onToggle} label={`Botão ${label}`} />
            <span className="pset-button-name">{label}</span>
            <input
                type="text"
                className="input3"
                aria-label={`Texto do botão ${label}`}
                placeholder={placeholder}
                value={textValue}
                disabled={!checked}
                onChange={(e) => onTextChange(e.target.value)}
            />
        </div>
    );
}
